import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { downloadCSV } from "@/lib/export-utils";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Building2,
  FileCheck,
  BookOpen,
  List,
  Info,
  ExternalLink,
  RefreshCw,
  Download,
  ChevronRight,
  ShieldAlert,
} from "lucide-react";

type ComplianceSeverity = "error" | "warning" | "info" | "ok";

interface ComplianceCheckResult {
  id: string;
  ferStandard: string;
  section: string;
  severity: ComplianceSeverity;
  title: string;
  titleGerman: string;
  description: string;
  recommendation?: string;
  legalReference: string;
}

interface ComplianceReport {
  generatedAt: number;
  companyName: string;
  fiscalYear: number;
  accountingStandard: string;
  overallCompliance:
    | "compliant"
    | "mostly_compliant"
    | "needs_attention"
    | "non_compliant";
  complianceScore: number;
  checks: ComplianceCheckResult[];
  errorCount: number;
  warningCount: number;
  infoCount: number;
  okCount: number;
  standardsSummary: {
    standard: string;
    status: ComplianceSeverity;
    checkCount: number;
  }[];
}

export default function SwissGAAPFER() {
  const { selectedCompanyId } = useCompany();
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear() - 1);
  const [hasRun, setHasRun] = useState(false);

  // Query for company data
  const { data: company, isLoading: isLoadingCompany } =
    trpc.company.get.useQuery(
      { id: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  // Compliance check query - enabled only after user clicks "Run Check"
  const {
    data: complianceData,
    isLoading: isChecking,
    refetch: runCheck,
    error: complianceError,
  } = trpc.cfo.runComplianceFromTransactions.useQuery(
    {
      companyId: selectedCompanyId!,
      fiscalYear,
    },
    {
      enabled: !!selectedCompanyId && hasRun,
      retry: false,
    }
  );

  const report: ComplianceReport | undefined = complianceData?.report;

  const handleRunCheck = () => {
    setHasRun(true);
    if (hasRun) {
      runCheck();
    }
  };

  const handleExportCSV = () => {
    if (!report) return;

    const header =
      "Standard,Section,Severity,Title,Title (German),Description,Recommendation,Legal Reference\n";
    const rows = report.checks.map(
      check =>
        `"${check.ferStandard}","${check.section}","${check.severity}","${escapeCSV(check.title)}","${escapeCSV(check.titleGerman)}","${escapeCSV(check.description)}","${escapeCSV(check.recommendation || "")}","${escapeCSV(check.legalReference)}"`
    );
    const csv = header + rows.join("\n") + "\n";

    const filename = `swiss-gaap-fer-compliance-${complianceData?.companyName || "company"}-${fiscalYear}.csv`;
    downloadCSV(csv, filename);
  };

  function escapeCSV(str: string): string {
    return str.replace(/"/g, '""');
  }

  const getOverallStatusLabel = (
    status: ComplianceReport["overallCompliance"]
  ) => {
    switch (status) {
      case "compliant":
        return "Compliant";
      case "mostly_compliant":
        return "Mostly Compliant";
      case "needs_attention":
        return "Needs Attention";
      case "non_compliant":
        return "Non-Compliant";
    }
  };

  const getOverallStatusColor = (
    status: ComplianceReport["overallCompliance"]
  ) => {
    switch (status) {
      case "compliant":
        return "text-success";
      case "mostly_compliant":
        return "text-info";
      case "needs_attention":
        return "text-warning";
      case "non_compliant":
        return "text-destructive";
    }
  };

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">No Company Selected</h2>
            <p className="text-muted-foreground">
              Please select a company to check Swiss GAAP FER compliance.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoadingCompany) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="space-y-8">
          {/* Preview Banner */}
          <Alert className="border-[var(--warning)] bg-warning/10">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              <strong>Preview</strong> -- Compliance checks are informational
              only, not legal advice. Consult a qualified Swiss auditor for
              official FER compliance assessment.
            </AlertDescription>
          </Alert>

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <FileCheck className="h-8 w-8 text-primary" />
                Swiss GAAP FER Compliance
              </h1>
              <p className="text-muted-foreground mt-2">
                Verify compliance with Swiss accounting standards for{" "}
                {company?.name}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Fiscal Year Selector */}
              <div className="flex items-center gap-2">
                <label
                  htmlFor="fiscal-year"
                  className="text-sm text-muted-foreground"
                >
                  Year:
                </label>
                <select
                  id="fiscal-year"
                  value={fiscalYear}
                  onChange={e => {
                    setFiscalYear(Number(e.target.value));
                    setHasRun(false);
                  }}
                  className="border rounded px-2 py-1 text-sm bg-background"
                >
                  {[...Array(5)].map((_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>

              {report && (
                <p className="text-sm text-muted-foreground">
                  Last checked:{" "}
                  {new Date(report.generatedAt).toLocaleString("de-CH")}
                </p>
              )}
              <Button
                variant="outline"
                onClick={handleRunCheck}
                disabled={isChecking}
              >
                {isChecking ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Run Check
              </Button>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={!report}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Error state */}
          {complianceError && (
            <Alert className="border-destructive bg-destructive/10">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                {complianceError.message ||
                  "Failed to run compliance check. Make sure transactions are imported for the selected year."}
              </AlertDescription>
            </Alert>
          )}

          {/* Loading state */}
          {isChecking && (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
                <h3 className="text-lg font-medium">
                  Running Compliance Checks...
                </h3>
                <p className="text-muted-foreground">
                  Analyzing {fiscalYear} financial data against Swiss GAAP FER
                  standards
                </p>
              </CardContent>
            </Card>
          )}

          {/* No data yet state */}
          {!hasRun && !isChecking && (
            <Card>
              <CardContent className="py-12 text-center">
                <FileCheck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  Ready to Check Compliance
                </h3>
                <p className="text-muted-foreground mb-4">
                  Select a fiscal year and click "Run Check" to analyze your
                  financial data against Swiss GAAP FER standards.
                </p>
                <Button onClick={handleRunCheck}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Run Check for {fiscalYear}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {report && !isChecking && (
            <>
              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-success/20">
                        <CheckCircle className="h-6 w-6 text-success" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{report.okCount}</p>
                        <p className="text-sm text-muted-foreground">
                          Compliant
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-warning/20">
                        <AlertTriangle className="h-6 w-6 text-warning" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">
                          {report.warningCount}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Warnings
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-destructive/20">
                        <XCircle className="h-6 w-6 text-destructive" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">
                          {report.errorCount}
                        </p>
                        <p className="text-sm text-muted-foreground">Errors</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="relative w-16 h-16">
                        <svg className="w-full h-full" viewBox="0 0 100 100">
                          <circle
                            className="text-muted stroke-current"
                            strokeWidth="10"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                          />
                          <circle
                            className={`stroke-current ${report.complianceScore >= 80 ? "text-success" : report.complianceScore >= 60 ? "text-warning" : "text-destructive"}`}
                            strokeWidth="10"
                            strokeLinecap="round"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            strokeDasharray={`${report.complianceScore * 2.51} ${100 * 2.51}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl font-bold">
                            {report.complianceScore}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <p
                          className={`font-semibold ${getOverallStatusColor(report.overallCompliance)}`}
                        >
                          {getOverallStatusLabel(report.overallCompliance)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {complianceData?.transactionCount} transactions
                          analyzed
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Compliance Details */}
              <Tabs defaultValue="all" className="w-full">
                <TabsList>
                  <TabsTrigger value="all">All Standards</TabsTrigger>
                  <TabsTrigger value="issues">Issues Only</TabsTrigger>
                  <TabsTrigger value="info">About FER</TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="space-y-6 mt-6">
                  {/* Group by FER standard summary */}
                  {report.standardsSummary.map(stdSummary => {
                    const checksForStandard = report.checks.filter(
                      c => c.ferStandard === stdSummary.standard
                    );
                    return (
                      <Card key={stdSummary.standard}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <List className="h-5 w-5" />
                            {stdSummary.standard.replace("_", " ")}
                            <Badge
                              className={
                                stdSummary.status === "ok"
                                  ? "bg-success/20 text-success"
                                  : stdSummary.status === "warning"
                                    ? "bg-warning/20 text-warning"
                                    : stdSummary.status === "error"
                                      ? "bg-destructive/20 text-destructive"
                                      : "bg-muted text-muted-foreground"
                              }
                            >
                              {stdSummary.checkCount} check
                              {stdSummary.checkCount !== 1 ? "s" : ""}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {checksForStandard.map(check => (
                              <div
                                key={check.id}
                                className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                              >
                                <div className="mt-0.5">
                                  {check.severity === "ok" && (
                                    <CheckCircle className="h-5 w-5 text-success" />
                                  )}
                                  {check.severity === "warning" && (
                                    <AlertTriangle className="h-5 w-5 text-warning" />
                                  )}
                                  {check.severity === "error" && (
                                    <XCircle className="h-5 w-5 text-destructive" />
                                  )}
                                  {check.severity === "info" && (
                                    <Info className="h-5 w-5 text-info" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline">
                                      {check.ferStandard.replace("_", " ")}
                                    </Badge>
                                    <h4 className="font-medium">
                                      {check.title}
                                    </h4>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {check.description}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {check.titleGerman}
                                  </p>
                                  {check.recommendation && (
                                    <div className="mt-2 p-2 bg-muted rounded text-sm flex items-start gap-2">
                                      <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
                                      <span>{check.recommendation}</span>
                                    </div>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Ref: {check.legalReference}
                                  </p>
                                </div>
                                <Badge
                                  className={
                                    check.severity === "ok"
                                      ? "bg-success/20 text-success"
                                      : check.severity === "warning"
                                        ? "bg-warning/20 text-warning"
                                        : check.severity === "error"
                                          ? "bg-destructive/20 text-destructive"
                                          : "bg-muted text-muted-foreground"
                                  }
                                >
                                  {check.severity === "ok" && "Pass"}
                                  {check.severity === "warning" && "Warning"}
                                  {check.severity === "error" && "Error"}
                                  {check.severity === "info" && "Info"}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </TabsContent>

                <TabsContent value="issues" className="space-y-6 mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Items Requiring Attention
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {report.checks
                          .filter(
                            c =>
                              c.severity === "warning" || c.severity === "error"
                          )
                          .map(check => (
                            <div
                              key={check.id}
                              className={`p-4 border rounded-lg ${check.severity === "error" ? "border-destructive/30 bg-destructive/10" : "border-warning/30 bg-warning/10"}`}
                            >
                              <div className="flex items-start gap-4">
                                {check.severity === "error" ? (
                                  <XCircle className="h-5 w-5 text-destructive mt-0.5" />
                                ) : (
                                  <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline">
                                      {check.ferStandard.replace("_", " ")}
                                    </Badge>
                                    <h4 className="font-medium">
                                      {check.title}
                                    </h4>
                                  </div>
                                  <p className="text-sm">{check.description}</p>
                                  {check.recommendation && (
                                    <div className="mt-2 p-2 bg-background rounded text-sm flex items-start gap-2">
                                      <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
                                      <span>{check.recommendation}</span>
                                    </div>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Ref: {check.legalReference}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        {report.checks.filter(
                          c =>
                            c.severity === "warning" || c.severity === "error"
                        ).length === 0 && (
                          <div className="text-center py-8 text-muted-foreground">
                            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
                            <p>No compliance issues found!</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="info" className="space-y-6 mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5" />
                        About Swiss GAAP FER
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p>
                        Swiss GAAP FER (Fachempfehlungen zur Rechnungslegung)
                        are the accounting standards issued by the Foundation
                        for Accounting and Reporting Recommendations in
                        Switzerland.
                      </p>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <h4 className="font-medium">
                            Who Must Apply Swiss GAAP FER?
                          </h4>
                          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                            <li>
                              Companies listed on SIX Exchange Regulation
                              (domestic standard)
                            </li>
                            <li>Large cooperatives and foundations</li>
                            <li>
                              Companies that voluntarily adopt these standards
                            </li>
                            <li>Public institutions requiring transparency</li>
                          </ul>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-medium">Key Benefits</h4>
                          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                            <li>Tailored for Swiss business environment</li>
                            <li>Less complex than IFRS</li>
                            <li>Cost-effective implementation</li>
                            <li>
                              Widely accepted by Swiss banks and investors
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="pt-4 border-t">
                        <Button variant="outline" asChild>
                          <a
                            href="https://www.fer.ch"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Visit Swiss GAAP FER Website
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
